import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Search, BookOpen, ScanBarcode } from 'lucide-react';
import axios from 'axios';
import { formatDate } from '../../utils/dateTimeUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : ''
  };
};

const IssueBook = () => {
  const [books, setBooks] = useState([]);
  const [members, setMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [accessionSearch, setAccessionSearch] = useState('');
  const [selectedBook, setSelectedBook] = useState(null);
  const [selectedCopy, setSelectedCopy] = useState(null);
  const [availableCopies, setAvailableCopies] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dueDays, setDueDays] = useState(14);
  const [issueDescription, setIssueDescription] = useState('');

  useEffect(() => {
    fetchBooks();
    fetchMembers();
  }, []);

  const fetchBooks = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/library/books?limit=10000`, { headers: getAuthHeaders() });
      let data = [];
      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data?.data) {
        data = Array.isArray(response.data.data) ? response.data.data : [];
      }
      setBooks(data.filter(book => book.available_copies > 0));
    } catch (error) {
      console.error('Error fetching books:', error);
      setBooks([]);
    }
  };

  const fetchMembers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/library/members`, { headers: getAuthHeaders() });
      const data = Array.isArray(response.data) ? response.data : [];
      setMembers(data.filter(member => !member.is_blocked));
    } catch (error) {
      console.error('Error fetching members:', error);
      setMembers([]);
    }
  };

  const fetchBookCopies = async (bookId) => {
    try {
      const response = await axios.get(`${API_URL}/api/library/books/${bookId}/copies`, { headers: getAuthHeaders() });
      const copies = Array.isArray(response.data) ? response.data : [];
      const available = copies.filter(c => c.status === 'Available');
      setAvailableCopies(available);
      if (available.length > 0) {
        setSelectedCopy(available[0]); // Auto-select first available copy
      }
    } catch (error) {
      console.error('Error fetching copies:', error);
      setAvailableCopies([]);
    }
  };

  const handleSelectBook = (book) => {
    setSelectedBook(book);
    setSelectedCopy(null);
    setAvailableCopies([]);
    setAccessionSearch('');
    fetchBookCopies(book.id);
  };

  const handleAccessionSearch = async () => {
    if (!accessionSearch.trim()) return;
    try {
      // Search across all book copies by accession number
      const response = await axios.get(`${API_URL}/api/library/accession-register?search=${accessionSearch.trim()}`, { headers: getAuthHeaders() });
      const data = response.data?.copies || [];
      const match = data.find(c =>
        c.accession_number?.toLowerCase() === accessionSearch.trim().toLowerCase() && c.status === 'Available'
      );
      if (match) {
        // Find the book in our loaded books
        const book = books.find(b => b.id === match.book_id);
        if (book) {
          setSelectedBook(book);
          setSelectedCopy({ id: match.id, accession_number: match.accession_number, copy_number: match.copy_number, status: match.status });
          setAvailableCopies([{ id: match.id, accession_number: match.accession_number, copy_number: match.copy_number, status: match.status }]);
          setSearchTerm('');
        } else {
          alert(`Book found but not available for issue`);
        }
      } else {
        const unavailable = data.find(c => c.accession_number?.toLowerCase() === accessionSearch.trim().toLowerCase());
        if (unavailable) {
          alert(`Copy ${accessionSearch} is currently "${unavailable.status}" - not available for issue`);
        } else {
          alert(`No copy found with accession number: ${accessionSearch}`);
        }
      }
    } catch (error) {
      alert('Error searching accession number');
    }
  };

  const handleIssueBook = async () => {
    if (!selectedBook || !selectedMember) {
      alert('Please select both a book and a member');
      return;
    }

    setLoading(true);
    try {
      const dueDate = new Date();
      dueDate.setDate(dueDate.getDate() + dueDays);

      const issueData = {
        book_id: selectedBook.id,
        book_title: selectedBook.title,
        member_id: selectedMember.id,
        member_name: selectedMember.user_name,
        member_card_number: selectedMember.member_card_number,
        member_type: selectedMember.user_type,
        copy_id: selectedCopy?.id || null,
        accession_number: selectedCopy?.accession_number || null,
        due_date: dueDate.toISOString().split('T')[0],
        issued_by: 'Current User',
        issue_description: issueDescription.trim() || null
      };

      await axios.post(`${API_URL}/api/library/issues`, issueData, { headers: getAuthHeaders() });
      const copyInfo = selectedCopy ? ` (Copy: ${selectedCopy.accession_number})` : '';
      alert(`Book issued successfully!${copyInfo}`);
      setSelectedBook(null);
      setSelectedCopy(null);
      setAvailableCopies([]);
      setSelectedMember(null);
      setSearchTerm('');
      setAccessionSearch('');
      setIssueDescription('');
      fetchBooks();
    } catch (error) {
      console.error('Error issuing book:', error);
      alert(error.response?.data?.detail || 'Failed to issue book');
    } finally {
      setLoading(false);
    }
  };

  const filteredBooks = books.filter(book =>
    book.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    book.author_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (book.isbn || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6 space-y-6" data-testid="issue-book-page">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            Issue Book
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Accession Number Quick Search */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <label className="block text-sm font-semibold text-blue-800 mb-2">
              <ScanBarcode className="w-4 h-4 inline mr-1" />
              Scan / Enter Accession Number
            </label>
            <div className="flex gap-2">
              <Input
                placeholder="Enter accession number (e.g., ACC-2026-00001)"
                value={accessionSearch}
                onChange={(e) => setAccessionSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAccessionSearch()}
                className="bg-white"
                data-testid="accession-number-input"
              />
              <Button onClick={handleAccessionSearch} variant="outline" data-testid="accession-search-btn">
                <Search className="w-4 h-4 mr-1" /> Find
              </Button>
            </div>
            <p className="text-xs text-blue-600 mt-1">Enter or scan the accession number to directly find the exact copy</p>
          </div>

          {/* OR Divider */}
          <div className="flex items-center gap-4">
            <div className="flex-1 border-t border-gray-300"></div>
            <span className="text-sm text-gray-500 font-medium">OR search by title</span>
            <div className="flex-1 border-t border-gray-300"></div>
          </div>

          {/* Book Search Section */}
          <div>
            <label className="block text-sm font-medium mb-2">Search & Select Book</label>
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search by title, author, or ISBN..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="book-search-input"
              />
            </div>

            {searchTerm && (
              <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
                {filteredBooks.length === 0 ? (
                  <p className="p-4 text-gray-500">No available books found</p>
                ) : (
                  filteredBooks.map((book) => (
                    <div
                      key={book.id}
                      onClick={() => handleSelectBook(book)}
                      className={`p-3 border-b cursor-pointer hover:bg-gray-50 ${selectedBook?.id === book.id ? 'bg-blue-50 border-blue-200' : ''}`}
                      data-testid={`book-option-${book.id}`}
                    >
                      <div className="font-medium">{book.title}</div>
                      <div className="text-sm text-gray-600">by {book.author_name} | ISBN: {book.isbn}</div>
                      <div className="text-xs text-green-600">Available: {book.available_copies} / {book.total_copies}</div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Selected Book + Copy Selection */}
            {selectedBook && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg space-y-3">
                <div>
                  <h3 className="font-medium text-blue-900">Selected Book:</h3>
                  <p className="text-blue-800">{selectedBook.title} by {selectedBook.author_name}</p>
                </div>

                {/* Available Copies */}
                {availableCopies.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-blue-800 mb-1">Select Copy (Accession Number):</label>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                      {availableCopies.map((copy) => (
                        <button
                          key={copy.id}
                          onClick={() => setSelectedCopy(copy)}
                          className={`p-2 rounded border text-sm text-left transition-all ${
                            selectedCopy?.id === copy.id
                              ? 'bg-blue-600 text-white border-blue-700'
                              : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                          }`}
                          data-testid={`copy-option-${copy.accession_number}`}
                        >
                          <div className="font-mono font-medium text-xs">{copy.accession_number}</div>
                          <div className="text-xs opacity-75">Copy #{copy.copy_number}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {selectedCopy && (
                  <div className="bg-green-100 p-2 rounded text-sm text-green-800">
                    Issuing: <strong>{selectedCopy.accession_number}</strong> (Copy #{selectedCopy.copy_number})
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Member Selection */}
          <div>
            <label className="block text-sm font-medium mb-2">Select Member</label>
            <Select
              value={selectedMember?.id || ''}
              onValueChange={(memberId) => {
                const member = members.find(m => m.id === memberId);
                setSelectedMember(member);
              }}
            >
              <SelectTrigger data-testid="member-select">
                <SelectValue placeholder="Select a member" />
              </SelectTrigger>
              <SelectContent>
                {members.map((member) => (
                  <SelectItem key={member.id} value={member.id}>
                    {member.user_name} ({member.member_card_number}) - {member.user_type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {selectedMember && (
              <div className="mt-4 p-4 bg-green-50 rounded-lg">
                <h3 className="font-medium text-green-900">Selected Member:</h3>
                <p className="text-green-800">{selectedMember.user_name} - Card: {selectedMember.member_card_number}</p>
                <p className="text-sm text-green-600">Type: {selectedMember.user_type}</p>
              </div>
            )}
          </div>

          {/* Due Date */}
          <div>
            <label className="block text-sm font-medium mb-2">Due Date (Days from today)</label>
            <Input type="number" value={dueDays} onChange={(e) => setDueDays(parseInt(e.target.value))} min="1" max="90" data-testid="due-days-input" />
            <p className="text-sm text-gray-500 mt-1">
              Due date: {formatDate(Date.now() + dueDays * 24 * 60 * 60 * 1000)}
            </p>
          </div>

          {/* Description / Note */}
          <div>
            <label className="block text-sm font-medium mb-2">Issue Description / Note</label>
            <textarea
              data-testid="issue-description-input"
              value={issueDescription}
              onChange={(e) => setIssueDescription(e.target.value)}
              placeholder="Enter any note about this issue (e.g., book condition, purpose, special instructions)..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm min-h-[80px] resize-y focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={3}
            />
          </div>

          {/* Issue Button */}
          <Button onClick={handleIssueBook} disabled={!selectedBook || !selectedMember || loading} className="w-full" data-testid="issue-book-btn">
            {loading ? 'Issuing...' : `Issue Book${selectedCopy ? ` (${selectedCopy.accession_number})` : ''}`}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default IssueBook;
